import React, { useEffect, useState } from "react";
import { saveAs } from "file-saver";
import { getUserCredits, generateImageVariation } from "../../api/businesses";
import { useCredits } from "../../context/CreditsContext";

interface GeneratedImage {
  beforeUrl: string;
  afterUrl: string;
  render_id: string;
  documentId: string;
  variationCount?: number;
  style: string;
  room_type: string;
}

interface GeneratedResultsProps {
  generatedImages: GeneratedImage[];
  onGenerateNew: () => void;
  isDeclutter?: boolean;
}

const MAX_VARIATIONS = 20;

const GeneratedResults: React.FC<GeneratedResultsProps> = ({
  generatedImages,
  onGenerateNew,
  isDeclutter = false, // default to false
}) => {
  const { setCredits } = useCredits();
  const [images, setImages] = useState<GeneratedImage[]>(
    generatedImages.map((img) => ({ ...img, variationCount: 1 }))
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const updateCredits = async () => {
      try {
        const res = await getUserCredits();
        setCredits(res.data.credits);
      } catch (error) {
        console.error("Error updating credits:", error);
      }
    };
    updateCredits();
  }, [setCredits]);

  const handleDownloadImage = () => {
    if (images[0]?.afterUrl) {
      saveAs(images[0].afterUrl, "final_image.jpg");
    } else {
      alert("No image available for download.");
    }
  };

  const handleGenerateVariation = async () => {
    const image = images[0];

    if ((image.variationCount || 0) >= MAX_VARIATIONS) {
      alert("You’ve reached the maximum of variations for this image.");
      return;
    }

    try {
      setIsLoading(true);
      const { render_id, documentId, room_type, style } = image;

      const response: any = await generateImageVariation({
        render_id,
        documentId,
        room_type,
        style,
      });

      const updatedImage = {
        ...image,
        afterUrl: response.data.result_image_url,
        variationCount: (image.variationCount || 0) + 1,
      };

      setImages([updatedImage]);
    } catch (error) {
      console.error("Error generating variation:", error);
      alert("Failed to generate image variation.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center w-full h-screen justify-center p-6">
      <h2
        className={`text-lg font-bold mb-4 text-center ${
          isLoading ? "text-yellow-500" : "text-green-600"
        }`}
      >
        {isLoading
          ? "Generating variation..."
          : "Image successfully generated!"}
      </h2>

      <div className="relative flex items-center justify-center w-full h-3/4 mb-4">
        {images[0] && (
          <>
            <img
              src={images[0].afterUrl}
              alt="Furnished Image"
              className={`max-w-full max-h-full object-contain rounded-lg shadow-lg transition-opacity duration-300 ${
                isLoading ? "opacity-50" : "opacity-100"
              }`}
            />
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-t-transparent border-white rounded-full animate-spin" />
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex space-x-4 items-center">
        <button
          onClick={onGenerateNew}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          disabled={isLoading}
        >
          Generate New
        </button>
        {!isDeclutter && (
          <div className="relative inline-block group">
            <button
              onClick={handleGenerateVariation}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              disabled={isLoading}
            >
              {isLoading ? "Applying style..." : "Try a New Look"}
            </button>

            {!isLoading && (
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-semibold px-1.5 py-0.5 rounded-full">
                New
              </span>
            )}

            {/* Tooltip */}
            {!isLoading && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 text-sm text-white bg-gray-700 p-2 rounded-md opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-lg text-center">
                Try another version of this image. No credits will be used.
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleDownloadImage}
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          disabled={isLoading}
        >
          Download Image
        </button>
      </div>
    </div>
  );
};

export default GeneratedResults;
